import type { QueryClient } from "@tanstack/react-query";
import {
  addOptimisticManualRows,
  buildOptimisticSpreadsheetCells,
  patchSpreadsheetSheetCells,
  reconcileBulkPasteSheetCells,
  rollbackOptimisticPasteCells,
} from "./spreadsheet-cache";
import { aiScriptSpreadsheetSheetKey } from "./spreadsheet-sheet-query";
import type {
  IAiSpreadsheetCell,
  IAiSpreadsheetPasteEdit,
  IAiSpreadsheetSheetResponse,
  IManualRowInput,
} from "./types";

export interface OptimisticBulkPasteContext {
  sheetKey: ReturnType<typeof aiScriptSpreadsheetSheetKey>;
  rollbacks: { prior: IAiSpreadsheetCell; optimistic: IAiSpreadsheetCell }[];
  optimisticToken: string;
  editsManualRows: boolean;
}

export const applyOptimisticBulkPaste = async ({
  queryClient,
  businessId,
  runId,
  sheetId,
  edits,
  rows,
}: {
  queryClient: QueryClient;
  businessId: string | null;
  runId: string;
  sheetId: string;
  edits: IAiSpreadsheetPasteEdit[];
  rows: IManualRowInput[];
}): Promise<OptimisticBulkPasteContext> => {
  const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
  await queryClient.cancelQueries({ queryKey: sheetKey });

  const sheet = queryClient.getQueryData<IAiSpreadsheetSheetResponse>(sheetKey);
  const loadedCells = sheet?.sheet.cells ?? [];
  const editedCellIds = new Set(edits.map((edit) => edit.cell_id));
  const editsManualRows = loadedCells.some(
    (cell) => cell.manual_row_id && editedCellIds.has(cell.id),
  );
  const optimisticCells = buildOptimisticSpreadsheetCells(loadedCells, edits);
  const cellsById = new Map(loadedCells.map((cell) => [cell.id, cell]));
  const rollbacks = optimisticCells.flatMap((optimistic) => {
    const prior = cellsById.get(optimistic.id);
    return prior ? [{ prior, optimistic }] : [];
  });

  if (optimisticCells.length > 0) {
    queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
      patchSpreadsheetSheetCells(data, optimisticCells),
    );
  }

  const optimisticToken = crypto.randomUUID();
  if (rows.length > 0) {
    queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
      addOptimisticManualRows(data, {
        sheetId,
        rowInputs: rows,
        token: optimisticToken,
        afterRow: rows[0]?.anchor?.after_row ?? null,
      }),
    );
  }

  return { sheetKey, rollbacks, optimisticToken, editsManualRows };
};

export const rollbackOptimisticBulkPaste = (
  queryClient: QueryClient,
  context: OptimisticBulkPasteContext,
) => {
  queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
    context.sheetKey,
    (data) => {
      const rolledBack = rollbackOptimisticPasteCells(data, context.rollbacks);
      return reconcileBulkPasteSheetCells(
        rolledBack,
        [],
        context.optimisticToken,
      );
    },
  );
};

export const clearOptimisticBulkPasteRows = (
  queryClient: QueryClient,
  context: OptimisticBulkPasteContext,
) => {
  queryClient.setQueryData<IAiSpreadsheetSheetResponse>(
    context.sheetKey,
    (data) => reconcileBulkPasteSheetCells(data, [], context.optimisticToken),
  );
};
